/**
 * Swamithra — WhatsApp number normalisation.
 *
 * The entry screen is the whole of authentication, so the number typed into it
 * IS the account key. US-00-001.12 spells out the consequence: `+91 98765 43210`,
 * `09876543210` and `9876543210` are one account, not three, and the same
 * normalisation has to run on web, on the panel and in the Expo app — which is
 * why it is here and not in a form component.
 *
 * NORMALISATION IS NOT VALIDATION. `normaliseMobileNumber` returns the best
 * E.164 rendering it can; `mobileNumberSchema` is what decides whether that
 * rendering is acceptable. Keeping them separate means the input can be
 * normalised on every keystroke without the field screaming at a half-typed
 * number.
 */

import { z } from "zod"

/**
 * Assumed when the user types a bare national number.
 *
 * India is the default because that is the market; a number typed WITH a `+` is
 * never re-interpreted, so a non-Indian E.164 number is accepted unchanged
 * (US-00-001.12).
 */
export const DEFAULT_CALLING_CODE = "91"

/**
 * E.164: a `+`, a non-zero country code digit, then up to fourteen more digits.
 *
 * The lower bound of eight total digits is deliberate — shorter than that is a
 * short code or a typo, never a mobile number capable of receiving WhatsApp.
 */
const E164_RE = /^\+[1-9]\d{7,14}$/

/** Digits in an Indian mobile number once the country code is removed. */
const INDIAN_NATIONAL_DIGITS = 10

/** True when the value is already a well-formed E.164 number. */
export function isE164(value: string): boolean {
	return E164_RE.test(value)
}

/**
 * Characters a person may legitimately type between the digits of a number.
 *
 * Spaces (including the non-breaking one a WhatsApp paste brings with it),
 * hyphens, brackets, dots and slashes are formatting. ANYTHING ELSE IS NOT A
 * NUMBER: `98765abc12` is a typo, and stripping the letters out of it produces
 * `+919876512`, which is a different, plausible-looking number that the entry
 * screen would then happily send a code to (US-00-001.12).
 */
const SEPARATORS_RE = /^[+\d\s\u00a0().\-/]*$/

/**
 * True when a number is one this product can send a WhatsApp code to.
 *
 * E.164 syntax is necessary but not sufficient: `+91984501234` is syntactically
 * fine and is not a number — India's national number is exactly ten digits, and
 * a stored contact value one digit short must be dropped rather than dialled
 * (US-00-005.15). The length rule is applied only to the country code whose
 * national length this package knows; every other country is accepted on E.164
 * syntax alone, because shipping a libphonenumber metadata table to a mobile
 * bundle to check Germany is not a trade this product makes.
 */
export function isValidMobileNumber(value: string): boolean {
	if (!isE164(value)) return false
	const digits = value.slice(1)
	if (digits.startsWith(DEFAULT_CALLING_CODE)) {
		return digits.length === DEFAULT_CALLING_CODE.length + INDIAN_NATIONAL_DIGITS
	}
	return true
}

/**
 * Renders user input as E.164.
 *
 * The rules, in the order they are applied:
 *
 *   1. Everything that is not a digit or a leading `+` is dropped — spaces,
 *      hyphens, brackets and the non-breaking spaces that come out of a paste
 *      from WhatsApp itself.
 *   2. `00` at the front is the international access prefix, and means `+`.
 *   3. A leading `+` is authoritative: the caller has stated the country and we
 *      do not second-guess it.
 *   4. Leading zeros on a bare national number are trunk-dial prefixes and are
 *      stripped (`09876543210` → `9876543210`).
 *   5. A bare number that ALREADY begins with the default calling code and is
 *      longer than a national number is treated as country code + national
 *      (`919876543210` → `+919876543210`). Without this rule the commonest way
 *      people write their own number produces a second account.
 *   6. Anything else gets the default calling code.
 *
 * Returns the normalised string even when it is not valid E.164 — validation is
 * `mobileNumberSchema`'s job, and a field that normalises mid-typing must not
 * throw.
 */
export function normaliseMobileNumber(raw: string, callingCode = DEFAULT_CALLING_CODE): string {
	const trimmed = raw.trim()

	// Rule 0, and it comes first: input that is not made of digits and formatting
	// is returned UNTOUCHED, so it fails validation as what it is. Dropping the
	// letters instead turns `98765abc12` into a well-formed number belonging to
	// somebody else.
	if (!SEPARATORS_RE.test(trimmed)) return trimmed

	const hasPlus = trimmed.startsWith("+")
	let digits = trimmed.replace(/\D/g, "")

	if (!hasPlus && digits.startsWith("00")) {
		return `+${digits.slice(2)}`
	}
	if (hasPlus) {
		// A `+` is authoritative about the COUNTRY, but not about a trunk-dial
		// zero someone left in front of the national part — `+91 09888700001`
		// is the same account as `9888700001`, not a thirteen-digit number
		// (TC-US-00-001-2-1, US-00-001.12). Only the default calling code's
		// length is known here, so this only fires for a number that is
		// unambiguously Indian by its country code.
		if (digits.startsWith(callingCode) && digits.length > callingCode.length) {
			const national = digits.slice(callingCode.length).replace(/^0+/, "")
			return `+${callingCode}${national}`
		}
		return `+${digits}`
	}

	digits = digits.replace(/^0+/, "")
	if (digits.length === 0) return ""

	// Rule 5. `NATIONAL_NUMBER_LENGTH` is not knowable for every country without a
	// full libphonenumber metadata table, which is 500 kB we are not shipping to a
	// mobile bundle. The check is therefore limited to the DEFAULT code, where the
	// national number length is known to be ten.
	if (
		callingCode === DEFAULT_CALLING_CODE &&
		digits.startsWith(callingCode) &&
		digits.length > INDIAN_NATIONAL_DIGITS
	) {
		return `+${digits}`
	}

	return `+${callingCode}${digits}`
}

/**
 * The field schema for every mobile-number input in the product.
 *
 * It normalises first and validates second, so the value that reaches the API —
 * and therefore the value stored and looked up — is always E.164
 * (US-00-001.12, US-01-005.15).
 */
export const mobileNumberSchema = z
	.string()
	.trim()
	.min(1, "Enter your WhatsApp number")
	.transform(value => normaliseMobileNumber(value))
	.refine(isValidMobileNumber, "Enter a valid WhatsApp number, including the country code.")

/**
 * The same rule for a value that is ALREADY E.164 — a server response, or a
 * number being echoed back into a request.
 *
 * Separate from `mobileNumberSchema` because a response that is not already
 * normalised is a server bug, and quietly fixing it here would hide it.
 */
export const e164Schema = z.string().regex(E164_RE, "Expected an E.164 mobile number.")

/**
 * Renders a number for display: `+91 98765 43210`.
 *
 * Grouping is cosmetic and applied only to Indian numbers, where the 5-5 split
 * is what people recognise. Everything else is returned as stored rather than
 * grouped wrongly.
 */
export function formatMobileNumber(e164: string): string {
	if (!isE164(e164)) return e164
	const withoutPlus = e164.slice(1)
	if (withoutPlus.startsWith(DEFAULT_CALLING_CODE)) {
		const national = withoutPlus.slice(DEFAULT_CALLING_CODE.length)
		if (national.length === INDIAN_NATIONAL_DIGITS) {
			return `+${DEFAULT_CALLING_CODE} ${national.slice(0, 5)} ${national.slice(5)}`
		}
	}
	return e164
}

/**
 * The `tel:` target for the Personalised Services banner (US-00-005.3/.5).
 *
 * E.164 with no spaces: a dialler given a grouped number on Android drops the
 * prefill silently.
 */
export function telHref(e164: string): string {
	return `tel:${e164}`
}

/** The `mailto:` target for the same banner. */
export function mailtoHref(email: string): string {
	return `mailto:${email}`
}
